import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "./LangContext";
import { useTenant } from "./TenantContext";
import { BORGO_IMAGES, DIGITAL_TWIN_IMAGE, ATTRAZIONI_CARDS } from "./data";
import { MapPin, Calendar, Landmark, Palette, Trees, Sparkles, Home as HomeIcon, ArrowRight } from "lucide-react";
import { HashLink } from "./HashLink";
import PublicImmobiliMap from "./components/PublicImmobiliMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function IlBorgo() {
    const { t } = useLang();
    const { tenant, slug } = useTenant();
    const borgo = tenant?.borgo || {};
    // Only fall back to the built-in BORGO_IMAGES for the default Tadasuni tenant.
    // Every other borgo shows either its own uploaded gallery or a clear empty-state
    // placeholder — so we never leak Tadasuni photos onto e.g. Dozza's homepage.
    const isTadasuni = (slug || "tadasuni") === "tadasuni";
    const hasCustomGallery = Array.isArray(borgo.gallery) && borgo.gallery.length > 0;
    const images = (hasCustomGallery ? borgo.gallery : (isTadasuni ? BORGO_IMAGES : [])).slice(0, 4);
    return (
        <section id="il-borgo" data-testid="il-borgo-section" className="py-24 md:py-32 px-6 lg:px-10 bg-stone-50">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                <div className="fade-up">
                    <div className="text-xs font-semibold tracking-[0.3em] uppercase text-emerald-700 mb-4">
                        IL BORGO
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-stone-900 leading-tight">
                        {borgo.title || t.borgo_title}
                    </h2>
                    <p className="mt-6 text-stone-600 text-lg leading-relaxed max-w-lg">
                        {borgo.description || t.borgo_desc}
                    </p>
                    <div className="mt-8 flex gap-4">
                        <div className="flex-1 border border-emerald-100 bg-emerald-50/70 rounded-2xl p-5" data-testid="stat-altitude">
                            <MapPin size={22} className="text-emerald-700 mb-2" />
                            <div className="font-serif text-3xl font-semibold text-stone-900">{borgo.altitude ? `${borgo.altitude}m` : "—"}</div>
                            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mt-1">
                                {t.stat_alt}
                            </div>
                        </div>
                        <div className="flex-1 border border-amber-100 bg-amber-50/70 rounded-2xl p-5" data-testid="stat-population">
                            <Calendar size={22} className="text-amber-700 mb-2" />
                            <div className="font-serif text-3xl font-semibold text-stone-900">{borgo.population ? `~${borgo.population}` : "—"}</div>
                            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mt-1">
                                {t.stat_pop}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 fade-up" style={{ animationDelay: "0.15s" }} data-testid="borgo-gallery">
                    {images.length > 0 ? images.map((src, i) => (
                        <div
                            key={i}
                            className={`overflow-hidden rounded-2xl border border-stone-200 ${
                                i % 2 === 0 ? "aspect-[4/3]" : "aspect-square"
                            } ${i === 0 ? "translate-y-4" : i === 3 ? "translate-y-4" : ""}`}
                        >
                            <img
                                src={src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`}
                                alt={`${tenant?.name || "Borgo"} ${i + 1}`}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                            />
                        </div>
                    )) : (
                        [0, 1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className={`overflow-hidden rounded-2xl border-2 border-dashed border-stone-300 bg-stone-100/50 flex items-center justify-center text-stone-400 ${
                                    i % 2 === 0 ? "aspect-[4/3]" : "aspect-square"
                                } ${i === 0 ? "translate-y-4" : i === 3 ? "translate-y-4" : ""}`}
                                data-testid={`borgo-gallery-placeholder-${i}`}
                            >
                                <div className="text-center px-4">
                                    <MapPin size={22} className="mx-auto mb-1 opacity-40" />
                                    <div className="text-[10px] font-semibold uppercase tracking-widest">Foto {i + 1}</div>
                                    {i === 0 && (
                                        <div className="text-[10px] mt-1 opacity-70">Aggiungi dalle impostazioni</div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
}

export function GemelloDigitale() {
    const { t } = useLang();
    const { slug } = useTenant();
    const [cfg, setCfg] = React.useState({ enabled: false, thumbnail: "", title: "", description: "" });
    React.useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/borgo-digital-twin?borgo=${slug}`)
            .then((r) => r.json()).then((d) => setCfg(d || {})).catch(() => {});
    }, [slug]);
    const thumb = cfg.thumbnail || DIGITAL_TWIN_IMAGE;
    return (
        <section
            id="gemello"
            data-testid="gemello-section"
            className="relative overflow-hidden py-24 md:py-32 px-6 lg:px-10"
            style={{ background: "linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)" }}
        >
            <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: `radial-gradient(circle at 20% 20%, #10b981 0%, transparent 40%), radial-gradient(circle at 80% 80%, #d97706 0%, transparent 40%)`,
            }} />
            <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                <div>
                    <div className="text-xs font-semibold tracking-[0.3em] uppercase text-emerald-300 mb-4">
                        3D · INTERACTIVE
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight">
                        {cfg.title || t.gemello_title}
                    </h2>
                    <p className="mt-6 text-emerald-50/85 text-lg leading-relaxed max-w-lg">
                        {cfg.description || t.gemello_sub}
                    </p>
                    <HashLink
                        to="/gemello-digitale"
                        className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-white text-emerald-800 font-semibold hover:bg-emerald-50 hover:-translate-y-0.5 transition-all shadow-lg"
                        data-testid="gemello-cta"
                    >
                        <Sparkles size={18} />
                        {t.gemello_cta}
                    </HashLink>
                </div>
                <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/3] shadow-2xl">
                    <img src={thumb} alt="Digital Twin" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-emerald-900/20" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="pulse-ring w-20 h-20 rounded-full bg-white/95 flex items-center justify-center">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="#059669">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                    {cfg.enabled && (
                        <span className="absolute top-4 right-4 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider animate-pulse">
                            LIVE 3D
                        </span>
                    )}
                </div>
            </div>
        </section>
    );
}

const iconFor = (name) => {
    const map = { church: Landmark, palette: Palette, trees: Trees };
    const Icon = map[name] || Landmark;
    return <Icon size={22} className="text-emerald-700" />;
};

export function AttrazioniPreview() {
    const { lang, t } = useLang();
    return (
        <section id="attrazioni" data-testid="attrazioni-section" className="py-24 md:py-32 px-6 lg:px-10 bg-white">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <div className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-700 mb-4">
                        SCOPRI · DISCOVER
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-stone-900">
                        {t.attrazioni_title}
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {ATTRAZIONI_CARDS.map((c, i) => (
                        <HashLink
                            to="/attrazioni"
                            key={i}
                            className="card-hover bg-white border border-stone-200 rounded-2xl overflow-hidden block"
                            data-testid={`attrazione-card-${i}`}
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img
                                    src={c.image}
                                    alt={c.title[lang]}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-6">
                                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                                    {iconFor(c.icon)}
                                </div>
                                <h3 className="font-serif text-2xl font-semibold text-stone-900 mb-2">{c.title[lang]}</h3>
                                <p className="text-stone-600 text-sm leading-relaxed">{c.desc[lang]}</p>
                            </div>
                        </HashLink>
                    ))}
                </div>
            </div>
        </section>
    );
}


// ============ ImmobiliMapTeaser (home) ============
export function ImmobiliMapTeaser() {
    const { lang } = useLang();
    const { slug, tenant } = useTenant();
    const [items, setItems] = useState([]);

    useEffect(() => {
        axios.get(`${API}/immobili`, { params: { borgo: slug } })
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    }, [slug]);

    const geo = items.filter(
        (i) =>
            typeof i?.ubicazione?.latitudine === "number" &&
            typeof i?.ubicazione?.longitudine === "number"
    );

    // Best-effort fallback center: tenant.location.coords if provided, else Tadasuni
    const fallbackCenter = (tenant?.location?.lat && tenant?.location?.lng)
        ? [tenant.location.lat, tenant.location.lng]
        : [40.0833, 8.9167];

    return (
        <section id="immobili-mappa" data-testid="immobili-map-teaser-section" className="py-24 md:py-32 px-6 lg:px-10 bg-stone-50">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
                    <div className="lg:col-span-2 fade-up">
                        <div className="text-xs font-semibold tracking-[0.3em] uppercase text-emerald-700 mb-4">
                            {lang === "it" ? "IMMOBILI · MAPPA" : "PROPERTIES · MAP"}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl font-semibold text-stone-900 leading-tight">
                            {lang === "it" ? "Ogni casa, sulla mappa del borgo" : "Every home, on the village map"}
                        </h2>
                        <p className="mt-5 text-stone-600 leading-relaxed">
                            {lang === "it"
                                ? "Esplora gli immobili disponibili direttamente sulla mappa del borgo. Clicca un pin per vedere la foto vetrina e la descrizione, o apri la scheda completa del fascicolo digitale."
                                : "Explore available properties directly on the village map. Click a pin to see the cover photo and description, or open the full digital dossier."}
                        </p>
                        <div className="mt-6 flex items-center gap-5">
                            <div className="flex items-center gap-2 text-sm text-stone-600">
                                <div className="w-3 h-3 rounded-full bg-emerald-600 ring-2 ring-emerald-200" />
                                <span><b className="text-stone-900">{geo.length}</b> {lang === "it" ? "immobili sulla mappa" : "on the map"}</span>
                            </div>
                        </div>
                        <HashLink
                            to="/immobili"
                            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm transition-colors"
                            data-testid="home-map-cta"
                        >
                            <HomeIcon size={15} />
                            {lang === "it" ? "Apri la vetrina immobili" : "Open properties showcase"}
                            <ArrowRight size={14} />
                        </HashLink>
                    </div>
                    <div className="lg:col-span-3">
                        <PublicImmobiliMap
                            items={items}
                            fallbackCenter={fallbackCenter}
                            height="420px"
                            variant="teaser"
                            borgoSlug={slug}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
